import Controller from '@ember/controller';
import { computed, action } from '@ember/object';
import { task } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import FacebookLeadAdsService, { FacebookBusinessPageData } from 'cio/services/facebook-lead-ads';
import AlertsService from 'cio/services/alerts';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import ApiErrorsService from 'cio/services/api-errors';
import { tracked } from '@glimmer/tracking';
import AdBlocker from 'cio/services/ad-blocker';
import EnvironmentService from 'cio/services/environment';
import AnalyticsService from 'cio/services/analytics';
import FeaturesService from 'ember-feature-flags/services/features';
import CustomFormsService from 'cio/services/custom-forms';
import CustomForm from 'cio/models/custom-form';
import RouterService from '@ember/routing/router-service';
import CampaignModel from 'cio/models/campaign';
import { FormType, isCustomForm } from 'cio/utils/forms';
import { FormIntegrationDetailPanes } from 'cio/components/forms/custom/integration-details';
import DS from 'ember-data';
import { isEmpty, isNone } from '@ember/utils';

export default class IntegrationsFormsController extends Controller {
  @service analytics!: AnalyticsService;
  @service adBlocker!: AdBlocker;
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service features!: FeaturesService;
  @service environment!: EnvironmentService;
  @service customForms!: CustomFormsService;
  @service router!: RouterService;
  @service store!: DS.Store;

  // query param for analytics -> points where integration page was opened from
  from: string | null = null;
  queryParams = ['from'];

  facebookFormCampaigns!: CampaignModel[];

  // relevant for enabling Facebook integration (connecting to Facebook Business Page):
  @tracked fbUserAccessToken: string | null = null;
  @tracked facebookAccountName: string | null = null;
  @tracked facebookPages: FacebookBusinessPageData[] | null = null;
  @tracked connectFormAfterFacebookSetupFinished = false;

  // relevant for connecting FB Lead Ads forms with Customer.io:
  @tracked isConnectingNewFacebookForm = false;
  @tracked facebookLeadAdsFormToConnect: FacebookLeadAdsForm | null = null;

  // relevant for connecting custom forms:
  @tracked customFormToConnect: CustomForm | null = null;

  // relevant for showing "Send form data" modal for custom/Typeform/Jotform forms
  @tracked sendCustomFormDataModalDetailsToShow: FormIntegrationDetailPanes | null = null;
  @tracked sendCustomFormDataModalForm: FacebookLeadAdsForm | CustomForm | null = null;

  // relevant to disconnecting connected/draft forms:
  @tracked formToDisconnect: FacebookLeadAdsForm | CustomForm | null = null;

  // "disconnect" confirmation modal state:
  @tracked showDisconnectPageModal = false;

  @tracked showFormProvidersModal = false;

  fbNextPageCursor = '';

  @computed('from')
  get fromWithDefault() {
    return this.from ?? 'Forms Page';
  }

  get #selectedFacebookPage() {
    return (this.facebookPages ?? []).find((p) => p.id === this.facebookLeadAds.credential?.pageId)?.id;
  }

  fetchMorePagesTask = task(async () => {
    if (!isEmpty(this.fbNextPageCursor)) {
      try {
        let { pages, morePages } = await this.facebookLeadAds.fetchMorePages(this.fbNextPageCursor);
        this.setProperties({ facebookPages: [...(this.facebookPages ?? []), ...pages], fbNextPageCursor: morePages });
      } catch (err) {
        this.apiErrors.report(err);
      }
    }
  });

  facebookSetupStartTask = task(async () => {
    try {
      let { userAccessToken, pages, facebookAccountName, morePages } = await this.facebookLeadAds.setupStart(
        this.fromWithDefault,
      );

      if (!this.facebookLeadAds.enabled) {
        // enabling inactive integration:
        this.setProperties({
          fbUserAccessToken: userAccessToken,
          facebookPages: pages,
          facebookAccountName,
          fbNextPageCursor: morePages,
        });
      } else {
        // re-enabling to refresh access token - fast-forward

        if (!isNone(morePages)) {
          this.fbNextPageCursor = morePages;
        }
        this.facebookPages = pages ?? [];

        // we have to get all pages available to the user, not just the first page:
        while (this.fbNextPageCursor && this.#selectedFacebookPage == null) {
          await this.fetchMorePagesTask.perform();
        }

        // validate re-connecting to the same page as currently selected:
        if (this.#selectedFacebookPage == null) {
          this.alerts.error(
            'facebookLeadAds.reconnect.error',
            'You must select the same Facebook page as currently connected.',
          );
          return;
        }

        this.setProperties({ fbUserAccessToken: userAccessToken });
        this.facebookSetupFinishTask.perform(this.#selectedFacebookPage);
      }
    } catch (e) {
      // ignore: a re-thrown error already handled by facebookLeadAds service
    }
  });

  facebookSetupFinishTask = task(async (facebookPageID: string) => {
    let { fbUserAccessToken } = this;
    if (!fbUserAccessToken) {
      return;
    }

    await this.facebookLeadAds.setupFinish({
      userAccessToken: fbUserAccessToken,
      pageId: facebookPageID,
      from: this.fromWithDefault,
    });

    // Connect new form if Facebook got enabled by clicking "Connect form" dropdown action:
    if (this.connectFormAfterFacebookSetupFinished && this.facebookLeadAds.connectableDisconnectedForms.length) {
      this.connectForm(null, 'facebook');
    }

    // setup is done, drop tmp. properties used for integration setup
    this.setProperties({
      fbUserAccessToken: null,
      facebookAccountName: null,
      facebookPages: null,
      connectFormAfterFacebookSetupFinished: false,
    });
  });

  @action facebookSetupCancel() {
    this.setProperties({
      fbUserAccessToken: null,
      facebookAccountName: null,
      facebookPages: null,
      connectFormAfterFacebookSetupFinished: false,
    });
  }

  disconnectPageTask = task(async () => {
    try {
      await this.facebookLeadAds.disable();

      this.store.unloadAll('facebook-lead-ads-form');
      this.set('showDisconnectPageModal', false);

      this.alerts.success('facebookLeadAds.disable.success', 'Facebook Lead Ads disabled');
    } catch (e) {
      this.apiErrors.report(e);
    }
  });

  @action disconnectPage() {
    this.analytics.track('facebook_lead_ads', 'disconnect_business_page_started');
    this.set('showDisconnectPageModal', true);
  }

  @action cancelFormModal() {
    this.set('isConnectingNewFacebookForm', false);

    if (!this.facebookLeadAdsFormToConnect) {
      return;
    }

    this.facebookLeadAdsFormToConnect.rollbackAttributes();
    this.set('facebookLeadAdsFormToConnect', null);
  }

  @action cancelDisconnectFormModal() {
    if (!this.formToDisconnect) {
      return;
    }

    this.formToDisconnect.rollbackAttributes();
    this.set('formToDisconnect', null);
  }

  @action
  async refreshDataForCustomForm(_form: CustomForm) {
    // refresh list of custom forms in the service:
    await this.customForms.fetchForms();

    // refresh to add fresh auto-created form segment to the store: used for linking in the table
    await this.store.query('segment', {});
  }

  @action
  async refreshSegmentsForForm(form: FacebookLeadAdsForm) {
    if (!form.formSegment) {
      // refresh to add fresh auto-created form segment to the store: used for linking in the table
      // this is necessary when form gets connected for the first time, its segment just got created
      await this.store.query('segment', {});
    }
  }

  @action cancelCustomFormModal() {
    if (this.customFormToConnect != null) {
      if (this.customFormToConnect.get('isNew')) {
        this.customFormToConnect.unloadRecord();
      } else {
        this.customFormToConnect.rollbackAttributes();
      }
    }
    this.set('customFormToConnect', null);
  }

  @action connectForm(f: FacebookLeadAdsForm | CustomForm | null, type: 'facebook' | 'custom') {
    this.analytics.track('forms', 'manage_form_started', {
      environmentId: this.environment.current.id,
      action: 'Connecting',
      formType: type === 'facebook' ? FormType.FacebookLeadAds : FormType.Custom,
      formId: f?.formId,
      api: f != null && isCustomForm(f) && f.isApiForm,
      scanned: f != null && isCustomForm(f) && f.isScannedForm,
    });

    if (type === 'facebook') {
      if (!this.facebookLeadAds.enabled) {
        this.connectFormAfterFacebookSetupFinished = true;
        return this.facebookSetupStartTask.perform();
      }

      this.set('isConnectingNewFacebookForm', true);
      if (f != null) {
        this.set('facebookLeadAdsFormToConnect', f);
      }
    } else if (type === 'custom') {
      this.set('customFormToConnect', f ?? this.store.createRecord('custom-form'));
    }

    return;
  }

  @action disconnectForm(f: FacebookLeadAdsForm | CustomForm) {
    this.analytics.track('forms', 'manage_form_started', {
      environmentId: this.environment.current.id,
      action: 'Disconnecting',
      formType: f.formType,
      formId: f.formId,
      api: isCustomForm(f) && f.isApiForm,
      scanned: isCustomForm(f) && f.isScannedForm,
    });

    this.set('formToDisconnect', f);
  }

  @action openFormProvidersModal() {
    this.showFormProvidersModal = true;
  }

  @action
  // form=null is explicitly passed, otherwise the click event would be send as the second param in some template usage context:
  // GOOD: {{on "click" (fn this.showCustomFormDataDetailsFor foo null)}} -> showCustomFormDataDetailsFor(foo, null)
  // BAD:  {{on "click" (fn this.showCustomFormDataDetailsFor foo)}}      -> showCustomFormDataDetailsFor(foo, PointerEvent)
  showCustomFormDataDetailsFor(pane: FormIntegrationDetailPanes, form: FacebookLeadAdsForm | CustomForm | null) {
    this.sendCustomFormDataModalDetailsToShow = pane;
    this.sendCustomFormDataModalForm = form;
  }

  @action
  closeCustomformDataModal() {
    this.sendCustomFormDataModalDetailsToShow = null;
    this.sendCustomFormDataModalForm = null;
  }

  @action
  setDefaults() {
    this.setProperties({
      fbUserAccessToken: null,
      facebookAccountName: null,
      facebookPages: null,

      facebookLeadAdsFormToConnect: null,
      customFormToConnect: null,
      formToDisconnect: null,

      showDisconnectPageModal: false,
    });
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'integrations/forms': IntegrationsFormsController;
  }
}
